/**
 * PIN VERSION ALERTS (Tom, 2026-10-17; payload VC-D): a holder's page whose
 * references reach the same island at different versions. Each conflict
 * names the island, the version this holder uses (`chosen`: its own pin) or
 * none (`chosen: null`: ambiguous — the server withholds that island's rows
 * on the page), and every version with the routes that reach it, nearest
 * first, each ending at the island (the holder's own pin is a one-element
 * route). An island the reader cannot read is `{hidden: true}` and is never
 * named.
 *
 * Kept per holder, as of its latest open (the open's requested entry carries
 * them); a holder's final release forgets them.
 */

import { useSyncExternalStore } from "react";

export type HiddenIsland = { hidden: true };
export type RouteIsland = string | HiddenIsland;
export type PinConflict = {
  island: string | HiddenIsland;
  type?: string;
  chosen: string | null;
  /** V33: `chosen` is this project's own pin, kept on purpose ("Keep as
   *  override") or an override only; an ambiguous conflict is never settled */
  settled?: boolean;
  /** `name`: the island root's name AT that commit (VC-D); absent when it
   *  has none, and on a hidden island (which lists no versions) */
  versions: { commit: string; via: RouteIsland[][]; name?: string }[];
};

const byHolder = new Map<string, PinConflict[]>();
const listeners = new Set<() => void>();
const NONE: PinConflict[] = [];

const notify = () => { for (const l of Array.from(listeners)) l(); };

/** What the holder's latest open answered (an absent list: no conflicts). */
export function setPinConflicts(holder: string, conflicts: unknown): void {
  const list = Array.isArray(conflicts) ? (conflicts.filter(isConflict) as PinConflict[]).map(byVersion) : NONE;
  if (!list.length && !byHolder.has(holder)) return;
  if (list.length) byHolder.set(holder, list);
  else byHolder.delete(holder);
  notify();
}

export function forgetPinConflicts(holder: string): void {
  if (byHolder.delete(holder)) notify();
}

export function pinConflictsOf(holder: string | null | undefined): PinConflict[] {
  return (holder && byHolder.get(holder)) || NONE;
}

export function usePinConflicts(holder: string | null | undefined): PinConflict[] {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => pinConflictsOf(holder),
    () => NONE,
  );
}

/** Each DISTINCT version once, with every route that reaches it, in the
 *  order first seen (VC-core's display rule, 2026-10-17): the server may list
 *  a version once per route — the holder's own pin beside a reference at the
 *  same commit — and the alert names one version used, never two. */
function byVersion(c: PinConflict): PinConflict {
  const merged = new Map<string, { commit: string; via: RouteIsland[][]; name?: string }>();
  for (const v of c.versions) {
    const at = merged.get(v.commit);
    if (at) { at.via.push(...(v.via ?? [])); if (!at.name && named(v.name)) at.name = v.name; }
    else merged.set(v.commit, { commit: v.commit, via: [...(v.via ?? [])], ...(named(v.name) ? { name: v.name } : {}) });
  }
  if (merged.size === c.versions.length) return c;
  return { ...c, versions: Array.from(merged.values()) };
}

const named = (n: unknown): n is string => typeof n === "string" && n.length > 0;

/** The island's name for the alert (VC-core 2026-10-17: the alert names the
 *  ambiguous island, though its rows are withheld): the version used, else
 *  the one name every version shares, else each version's name in turn.
 *  None (no version is named): null, and the alert says what it is. */
export function conflictName(c: PinConflict): string | null {
  const at = (commit: string | null) => c.versions.find((v) => v.commit === commit)?.name;
  if (named(at(c.chosen))) return at(c.chosen) as string;
  const names = Array.from(new Set(c.versions.map((v) => v.name).filter(named)));
  return names.length ? names.join(" / ") : null;
}

export function isHidden(x: unknown): x is HiddenIsland {
  return !!x && typeof x === "object" && (x as HiddenIsland).hidden === true;
}

function isConflict(c: unknown): boolean {
  const x = c as PinConflict;
  return !!x && typeof x === "object" && (typeof x.island === "string" || isHidden(x.island)) && Array.isArray(x.versions);
}

export function resetPinConflictsForTests(): void {
  byHolder.clear();
  notify();
}
